import React, { useState } from 'react';
import SafeIcon from '../../common/SafeIcon';
import { FiPrinter, FiDownload, FiSend, FiExternalLink, FiCheck } from 'react-icons/fi';
import { formatCurrency } from '../../utils/formatters';

export default function StatementPreview({ customer, activity, aging, dateRange }) {
  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);

  if (!customer) return null;

  const handlePrint = () => {
    window.print();
  };

  const handleEmailStatement = () => {
    setIsSending(true);

    setTimeout(() => {
      setIsSending(false);
      setIsSent(true);
      setTimeout(() => setIsSent(false), 3000);
    }, 1500);
  };

  return (
    <div className="flex max-h-[78vh] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm animate-in fade-in slide-in-from-bottom-3 duration-300">
      <div className="flex shrink-0 items-center justify-between border-b border-slate-200 bg-slate-50/70 px-4 py-3 print:hidden sm:px-5">
        <div className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400">
            Live preview
          </span>
        </div>

        <div className="flex gap-1.5 sm:gap-2">
          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-[11px] font-medium text-slate-600 transition hover:bg-slate-50 sm:px-3"
          >
            <SafeIcon icon={FiPrinter} />
            <span className="hidden sm:inline">Print</span>
          </button>
          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-[11px] font-medium text-slate-600 transition hover:bg-slate-50 sm:px-3"
          >
            <SafeIcon icon={FiDownload} />
            <span className="hidden sm:inline">PDF</span>
          </button>
          <button
            type="button"
            onClick={handleEmailStatement}
            disabled={isSending || isSent}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-[11px] font-medium text-white transition active:scale-[0.98] sm:px-3.5 ${
              isSent ? 'bg-emerald-500' : 'bg-slate-800 hover:bg-slate-900'
            } disabled:opacity-80`}
          >
            <SafeIcon icon={isSent ? FiCheck : FiSend} />
            <span>{isSending ? 'Sending...' : isSent ? 'Sent' : 'Email'}</span>
          </button>
        </div>
      </div>

      <div id="statement-doc" className="custom-scrollbar flex-1 overflow-y-auto bg-white p-6 sm:p-8 lg:p-9">
        <div className="mb-9 flex items-start justify-between border-b border-slate-100 pb-6">
          <div>
            <div className="mb-4 flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900">
                <span className="text-[15px] font-medium text-white">P</span>
              </div>
              <span className="text-[17px] font-medium tracking-tight text-slate-900">ProfitYo</span>
            </div>
            <div className="text-[12px] font-normal leading-relaxed text-slate-500">
              ProfitYo Financial Systems Inc.<br />
              123 Ledger Lane, Suite 100<br />
              New York, NY 10001
            </div>
          </div>

          <div className="text-right">
            <h1 className="text-[25px] font-medium tracking-tight text-slate-900">Statement</h1>
            <div className="mt-3">
              <p className="text-[10px] font-medium uppercase tracking-[0.1em] text-slate-400">
                Statement date
              </p>
              <p className="mt-1 text-[13px] font-medium text-slate-700">
                {new Date().toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        <div className="mb-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <h3 className="mb-2 text-[10px] font-medium uppercase tracking-[0.12em] text-slate-400">
              Customer details
            </h3>
            <div className="text-[12px]">
              <p className="mb-1 text-[15px] font-medium text-slate-800">{customer.name}</p>
              <p className="font-normal leading-relaxed text-slate-500">
                {customer.email && <>{customer.email}<br /></>}
                {customer.phone && <>{customer.phone}<br /></>}
                {customer.billing_address?.address1 || 'Address not provided'}
              </p>
            </div>
          </div>

          <div className="flex flex-col justify-center rounded-xl border border-emerald-100 bg-emerald-50/60 px-5 py-4 sm:items-end">
            <h3 className="text-[10px] font-medium uppercase tracking-[0.1em] text-emerald-700">
              Total amount due
            </h3>
            <p className="mt-1 text-[25px] font-medium tracking-tight text-emerald-700">
              {formatCurrency(aging.total)}
            </p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[620px]">
            <thead>
              <tr className="border-b border-slate-300">
                <th className="py-2.5 text-left text-[10px] font-medium uppercase tracking-[0.08em] text-slate-500">Date</th>
                <th className="py-2.5 text-left text-[10px] font-medium uppercase tracking-[0.08em] text-slate-500">Activity</th>
                <th className="py-2.5 text-left text-[10px] font-medium uppercase tracking-[0.08em] text-slate-500">Reference</th>
                <th className="py-2.5 text-right text-[10px] font-medium uppercase tracking-[0.08em] text-slate-500">Amount</th>
                <th className="py-2.5 text-right text-[10px] font-medium uppercase tracking-[0.08em] text-slate-500">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {activity.length === 0 ? (
                <tr>
                  <td colSpan="5" className="py-7 text-center text-[12px] font-normal text-slate-500">
                    No activity for this period.
                  </td>
                </tr>
              ) : (
                activity.map((item) => (
                  <tr key={item.id}>
                    <td className="py-2.5 text-[12px] font-normal text-slate-500">{item.date}</td>
                    <td className="py-2.5 text-[12px] font-medium text-slate-800">{item.type}</td>
                    <td className="py-2.5 text-[12px] font-normal text-blue-600">{item.ref || '-'}</td>
                    <td className={`py-2.5 text-right text-[12px] font-medium ${
                      item.amount < 0 ? 'text-rose-600' : 'text-slate-800'
                    }`}>
                      {formatCurrency(item.amount)}
                    </td>
                    <td className="py-2.5 text-right text-[12px] font-medium text-slate-800">
                      {formatCurrency(item.balance)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-8 border-t border-slate-200 pt-6 break-inside-avoid">
          <h3 className="mb-3 text-[10px] font-medium uppercase tracking-[0.12em] text-slate-500">
            Account aging analysis
          </h3>
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-5">
            {['Current', '1-30 Days', '31-60 Days', '61-90 Days', 'Over 90 Days'].map((label, index) => {
              const keys = ['current', '1-30', '31-60', '61-90', '90+'];
              const value = aging[keys[index]];

              return (
                <div key={label} className="rounded-lg border border-slate-200 bg-slate-50/70 p-3">
                  <p className="text-[9px] font-medium uppercase tracking-wide text-slate-400">{label}</p>
                  <p className={`mt-1 text-[12px] font-medium ${
                    value > 0 && index > 1 ? 'text-rose-600' : 'text-slate-700'
                  }`}>
                    {formatCurrency(value)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-8 flex items-end justify-between border-t border-slate-100 pt-5 break-inside-avoid print:hidden">
          <div className="group flex cursor-pointer items-center gap-2 text-slate-400 transition-colors hover:text-blue-600">
            <SafeIcon icon={FiExternalLink} className="text-base" />
            <div>
              <p className="text-[9px] font-medium uppercase tracking-[0.1em]">Customer portal</p>
              <p className="text-[11px] font-normal">Secure online payment link</p>
            </div>
          </div>
          <p className="text-[9px] font-medium uppercase tracking-[0.12em] text-slate-300">
            Thank you for your business
          </p>
        </div>
      </div>
    </div>
  );
}